import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, CheckCircle, XCircle, Bug, Zap, Database } from 'lucide-react';
import { toast } from 'sonner';
import { debugFrankSetup, testFrankConnectivity, FrankDebugInfo } from '@/utils/frankDebugUtils';

const FrankDebugPanel = () => {
  const [isDebugging, setIsDebugging] = useState(false);
  const [debugInfo, setDebugInfo] = useState<FrankDebugInfo | null>(null);
  const [connectivityTest, setConnectivityTest] = useState<any>(null);

  const runDebugAnalysis = async () => {
    setIsDebugging(true);
    try {
      const info = await debugFrankSetup();
      setDebugInfo(info);
      
      // If we have an API URL, test connectivity
      if (info.apiUrl) {
        const connectivity = await testFrankConnectivity(info.apiUrl);
        setConnectivityTest(connectivity);
      }
    } catch (error) {
      toast.error('Debug analysis failed');
      console.error('Debug analysis error:', error);
    } finally {
      setIsDebugging(false);
    }
  };

  const StatusBadge = ({ status, label }: { status: boolean; label: string }) => (
    <Badge variant={status ? "default" : "destructive"} className="flex items-center gap-1">
      {status ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label}
    </Badge>
  );

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bug className="w-5 h-5" />
          Frank AI Debug Panel
        </CardTitle>
        <CardDescription>
          Diagnose Frank AI configuration and connectivity issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button 
          onClick={runDebugAnalysis} 
          disabled={isDebugging}
          className="w-full"
        >
          {isDebugging ? 'Running Analysis...' : 'Run Debug Analysis'}
        </Button>

        {debugInfo && (
          <div className="space-y-4">
            <Separator />
            
            {/* Status Overview */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Database className="w-4 h-4" />
                Configuration Status
              </h3>
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={debugInfo.memberFound} label="Member Context" />
                <StatusBadge status={debugInfo.organizationFound} label="Organization" />
                <StatusBadge status={debugInfo.associationIdValid} label="Association ID" />
                <StatusBadge status={debugInfo.configFound} label="API Config" />
              </div>
            </div>

            {/* Detailed Information */}
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Member ID:</strong>
                  <p className="text-muted-foreground font-mono text-xs break-all">
                    {debugInfo.memberId || 'Not found'}
                  </p>
                </div>
                <div>
                  <strong>Organization ID:</strong>
                  <p className="text-muted-foreground font-mono text-xs break-all">
                    {debugInfo.organizationId || 'Not found'}
                  </p>
                </div>
                <div>
                  <strong>Association ID:</strong>
                  <p className="text-muted-foreground">
                    {debugInfo.associationId || 'Not found'}
                  </p>
                </div>
                <div>
                  <strong>API Enabled:</strong>
                  <p className="text-muted-foreground">
                    {debugInfo.isEnabled !== undefined ? (debugInfo.isEnabled ? 'Yes' : 'No') : 'Unknown'}
                  </p>
                </div>
              </div>

              {debugInfo.apiUrl && (
                <div>
                  <strong>API URL:</strong>
                  <p className="text-muted-foreground font-mono text-xs break-all">
                    {debugInfo.apiUrl}
                  </p>
                </div>
              )}
            </div>

            {/* Connectivity Test Results */}
            {connectivityTest && (
              <div className="space-y-2">
                <Separator />
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Connectivity Test
                </h3>
                <div className="flex items-center gap-2">
                  <StatusBadge status={connectivityTest.success} label="Connection" />
                  {connectivityTest.statusCode && (
                    <Badge variant="outline">
                      {connectivityTest.statusCode} {connectivityTest.statusText}
                    </Badge>
                  )}
                  {connectivityTest.responseTime && (
                    <Badge variant="outline">
                      {connectivityTest.responseTime}ms
                    </Badge>
                  )}
                </div>
                {connectivityTest.error && (
                  <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-md">
                    <AlertCircle className="w-4 h-4 text-destructive mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-destructive">Connection Error</p>
                      <p className="text-xs text-muted-foreground">{connectivityTest.error}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recommendations */}
            {debugInfo && (
              <div className="space-y-2">
                <Separator />
                <h3 className="text-sm font-medium">Recommendations</h3>
                <div className="space-y-2 text-sm">
                  {!debugInfo.memberFound && (
                    <div className="flex items-start gap-2 p-2 bg-amber-50 rounded-md">
                      <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                      <p>Member context not found. Try refreshing the page or logging in again.</p>
                    </div>
                  )}
                  {!debugInfo.associationIdValid && (
                    <div className="flex items-start gap-2 p-2 bg-red-50 rounded-md">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                      <p>Invalid association ID. Contact system administrator to verify organization configuration.</p>
                    </div>
                  )}
                  {!debugInfo.configFound && (
                    <div className="flex items-start gap-2 p-2 bg-blue-50 rounded-md">
                      <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5" />
                      <p>No Frank API configuration found. Set up Frank API settings in the admin panel.</p>
                    </div>
                  )}
                  {debugInfo.isEnabled === false && (
                    <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-md">
                      <AlertCircle className="w-4 h-4 text-gray-500 mt-0.5" />
                      <p>Frank API is disabled. Enable it in the Frank API settings.</p>
                    </div>
                  )}
                  {connectivityTest && !connectivityTest.success && (
                    <div className="flex items-start gap-2 p-2 bg-red-50 rounded-md">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                      <p>Cannot connect to Frank API. Verify the API URL and ensure the service is running.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FrankDebugPanel;